"use client";
import { memo } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { ClipLoader } from "react-spinners";
import { useTranslation } from "next-i18next";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { getCloudfrontUrl } from "../utils/mediaHelper";

const PortfolioSlider = memo(({ className = "", listData, loading, flag }) => {
  const router = useRouter();
  const lang = router.locale || "en";
  const { t } = useTranslation(["home", "common"]);

  return (
    <section
      className={`w-full flex flex-col box-border gap-6 md:gap-10 text-text-primary px-[24px] md:px-[60px] lg:px-[80px] xl:px-[80px] pb-[40px] md:pb-[40px] lg:pb-[60px] xl:pb-[60px] max-w-[1600px] mx-auto ${className}`}
    >
      {/* Heading + Arrows (unchanged) */}
      <div className="w-full flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
        <h2 className=" font-medium text-[28px] sm:text-[32px] lg:text-[40px] leading-[120%] m-0">
          {flag ? t("common:portfolio.title") : t("home:home.portfolio.title")}
        </h2>
        {lang === "ar" ? (
          <div className="flex items-center gap-4">
            <div className="swiper-button-next-custom cursor-pointer w-[44px] h-[44px] border border-solid border-black rounded-full flex items-center justify-center text-black bg-transparent hover:bg-black hover:text-white transition">
              <IoIosArrowForward size={20} />
            </div>
            <div className="swiper-button-prev-custom cursor-pointer w-[44px] h-[44px] border border-solid border-black rounded-full flex items-center justify-center text-black bg-transparent hover:bg-black hover:text-white transition">
              <IoIosArrowBack size={20} />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <div className="swiper-button-prev-custom cursor-pointer w-[44px] h-[44px] border border-solid border-black rounded-full flex items-center justify-center text-black bg-transparent hover:bg-black hover:text-white transition">
              <IoIosArrowBack size={20} />
            </div>
            <div className="swiper-button-next-custom cursor-pointer w-[44px] h-[44px] border border-solid border-black rounded-full flex items-center justify-center text-black bg-transparent hover:bg-black hover:text-white transition">
              <IoIosArrowForward size={20} />
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="w-full flex justify-center items-center py-20">
          <ClipLoader color="#EF4444" size={48} />
        </div>
      ) : (
        <Swiper
          dir={lang === "ar" ? "ltr" : undefined}
          modules={[Navigation, Autoplay]}
          spaceBetween={40}
          slidesPerView={1}
          breakpoints={{
            640: { slidesPerView: 1 },
            768: { slidesPerView: 2 },
            1024: { slidesPerView: 2.2 },
            1028: { slidesPerView: 2.2 },
          }}
          // autoplay={{ delay: 5000 }}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          navigation={{
            nextEl: ".swiper-button-next-custom",
            prevEl: ".swiper-button-prev-custom",
          }}
          className="w-full rounded-[24px]"
        >
          {Array.isArray(listData) &&
            listData.map((portfolio, index) => {
              const href = `/portfolio/${portfolio?.slug || portfolio?.slug_en}`;
              const src =
                getCloudfrontUrl(portfolio?.coverImage) ||
                "/project-items@3x.png";
              return (
                <SwiperSlide key={portfolio?._id}>
                  <Link
                    key={portfolio?._id}
                    href={href}
                    className="block no-underline text-inherit"
                    aria-label={portfolio?.title || "Portfolio details"}
                  >
                    <div className="relative w-full box-border h-[360px] rounded-[24px] p-6 text-5xl text-background-color-primary flex flex-col justify-end transition-all duration-500 ease-in-out overflow-hidden">
                      {/* ✅ Image layer (replaces backgroundImage) */}
                      <Image
                        src={src}
                        alt={
                          portfolio?.coverImageAlt ||
                          `Portfolio ${index + 1} Image`
                        }
                        fill
                        className="object-cover object-center z-0"
                        sizes="(max-width: 768px) 100vw, 50vw"
                        // priority={index === 0} // optional: improves LCP
                      />

                      {/* ✅ Your existing overlay */}
                      <div className="absolute inset-0 bg-black/25 z-0 rounded-[24px]" />

                      {portfolio?.brandLogo && (
                        <div className="absolute top-4 right-4 z-20 bg-[#ddd] rounded-full shadow-md w-[60px] h-[60px] flex items-center justify-center border border-white/60">
                          <img
                            src={getCloudfrontUrl(portfolio.brandLogo)}
                            alt={`${portfolio?.title || "Portfolio"} brand logo`}
                            className="w-[45px] h-[45px] object-contain rounded-full"
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        </div>
                      )}

                      {/* ✅ Your existing content */}
                      <div className="relative z-10 flex flex-col lg:flex-row items-end justify-between gap-4 w-full">
                        <h3 className="w-full text-[24px] leading-[140%] font-medium m-0">
                          {portfolio?.title}
                        </h3>

                        <button className="w-max no-underline self-end shrink-0 rounded-[50px] bg-white text-black text-[16px] font-medium py-3 px-6 cursor-pointer hover:bg-[#e72c46] hover:text-white transition duration-300 ease-in-out">
                          {t("common:btnTxt.view_details")}
                        </button>
                      </div>
                    </div>
                  </Link>
                </SwiperSlide>
              );
            })}
        </Swiper>
      )}
    </section>
  );
});

PortfolioSlider.propTypes = {
  className: PropTypes.string,
  listData: PropTypes.array,
  loading: PropTypes.bool,
};

export default PortfolioSlider;
